import type {
  EditorCharacterStylingInput,
  EditorTextBlockInput,
  EditorTextRunInput,
} from '@messenger/lotus/row-store'
import type { EditorTextBoxConversionReportCollector } from './report'
import {
  blockStylingOrNothing,
  foldCharacterStyling,
  tinyMceContextInside,
  unanimousLeafStyling,
  type TinyMceContext,
} from './styleFold'

/** The tabular field whose Lotus table prints more of each member than TinyMCE did. */
const ADDITIONAL_FAMILY_KEY = 'additional_family'

const cellRun = (
  fieldKey: string,
  cellIndex: number,
  styling: EditorCharacterStylingInput,
): EditorTextRunInput => ({
  kind: 'field',
  fieldKey,
  params: { cellIndex },
  styling,
})

/**
 * The style a stored cell or line painted in: the fold from `container`
 * down to it, then the style its text agrees on.
 */
const paintedStyling = (
  el: Element,
  container: Element,
  containerStyling: EditorCharacterStylingInput,
): EditorCharacterStylingInput => {
  const chain: Element[] = []
  for (
    let node: Element | null = el;
    node !== null && node !== container;
    node = node.parentElement
  )
    chain.unshift(node)
  const folded = chain.reduce(foldCharacterStyling, containerStyling)
  return unanimousLeafStyling(el, folded).styling
}

/**
 * A table of one tabular field over its whole column, the block the editor's
 * own table insert makes: a field run per stored cell or line, each in the
 * style that cell painted in, at least one run however many were stored.
 * The case paints the rows at render, so no stored value is kept.
 */
const tableBlock = (
  fieldKey: string,
  cells: readonly Element[],
  container: Element,
  inside: TinyMceContext,
): EditorTextBlockInput => {
  const blockStyling = blockStylingOrNothing(inside.block)
  return {
    kind: 'case_detail_table',
    ...(blockStyling === undefined ? {} : { blockStyling }),
    runs:
      cells.length === 0
        ? [cellRun(fieldKey, 0, inside.character)]
        : cells.map((cell, i) =>
            cellRun(
              fieldKey,
              i,
              paintedStyling(cell, container, inside.character),
            ),
          ),
  }
}

/**
 * A `data-case-detail-table` wrapper as its table block. TinyMCE rebuilt the
 * wrapper from the case on every load, so only its first table's cells
 * matter, and only for their styling.
 */
export const tinyMceCaseDetailTableBlock = (
  wrapper: Element,
  fieldKey: string,
  context: TinyMceContext,
): EditorTextBlockInput => {
  const table = wrapper.querySelector('table')
  return tableBlock(
    fieldKey,
    table === null ? [] : [...table.querySelectorAll('td')],
    wrapper,
    tinyMceContextInside(context, wrapper),
  )
}

/**
 * A block-level `data-case-detail` element of a tabular field, TinyMCE's
 * additional-family block and its like, as a table block with a run per
 * stored line. The additional-family table prints each member's city, state
 * and country besides the name and relation TinyMCE printed, and the report
 * says so.
 */
export const tinyMceColumnTableBlock = (
  el: Element,
  fieldKey: string,
  context: TinyMceContext,
  report: EditorTextBoxConversionReportCollector,
): EditorTextBlockInput => {
  if (fieldKey === ADDITIONAL_FAMILY_KEY)
    report.add({ kind: 'additional-family-more-detail' })
  const inside = tinyMceContextInside(context, el)
  const lines = [...el.querySelectorAll('p')]
  return lines.length === 0
    ? tableBlock(fieldKey, [], el, {
        ...inside,
        character: unanimousLeafStyling(el, inside.character).styling,
      })
    : tableBlock(fieldKey, lines, el, inside)
}
